/**
 * PermissionNavItem Component
 *
 * Wrapper component that hides navigation items based on RBAC segment access.
 * Use this to wrap menu items in the sidebar navigation to hide them from
 * users who don't have the required permission level.
 *
 * DESIGN DECISION: Nav items are hidden (not disabled) when user lacks access.
 * This follows the principle "hide what users can't use" from the design doc.
 *
 * Usage:
 *   // Hide Ship button if user doesn't have operator access to outbound
 *   <PermissionNavItem segment="outbound" minLevel="operator">
 *     <MenuItem text="Ship" url="/warehouse/start-shipping" icon={faShippingFast} />
 *   </PermissionNavItem>
 *
 *   // Simple viewer-level check (default)
 *   <PermissionNavItem segment="billing">
 *     <MenuItem text="Billing" url="/billing/company/" icon={faFileInvoiceDollar} />
 *   </PermissionNavItem>
 *
 * @see docs/projects/rbac/03-design-doc.md Section 9 - Frontend Navigation & Permission UX
 */

import React, { ReactNode } from 'react'
import { usePermissions } from '../../hooks/usePermissions'
import { BusinessSegment, FunctionRole } from '../../types/rbac'

interface PermissionNavItemProps {
  /**
   * Business segment required to view this nav item
   */
  segment: BusinessSegment

  /**
   * Minimum access level required (default: 'viewer')
   */
  minLevel?: FunctionRole

  /**
   * The navigation item(s) to render if permission check passes
   */
  children: ReactNode
}

export function PermissionNavItem({
  segment,
  minLevel = 'viewer',
  children,
}: PermissionNavItemProps): React.ReactElement | null {
  const { hasAccess } = usePermissions()

  // If user doesn't have access, hide the nav item entirely
  if (!hasAccess(segment, minLevel)) {
    return null
  }

  return <>{children}</>
}

export default PermissionNavItem
